import { FC } from "react";
import classNames from "classnames";
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { OverviewSourceUsageChartProps } from "../types";
import { MOCK_CHART_DATA, MOCK_CHART_SERIES } from "../const";
import { getSourceUsageYAxisDomain } from "../utils/getSourceUsageYAxisDomain";
import OverviewSourceUsageDot from "./OverviewSourceUsageDot/OverviewSourceUsageDot";
import OverviewSourceUsageTooltip from "./OverviewSourceUsageTooltip/OverviewSourceUsageTooltip";

import "../../../assets/css/library/recharts.css";
import styles from "./OverviewSourceUsageChart.module.css";



const OverviewSourceUsageChart: FC<OverviewSourceUsageChartProps> = ({
  className,
  data = MOCK_CHART_DATA,
  series = MOCK_CHART_SERIES,
  hideMoreSourcesLegend = false,
  hideLegend = false,
}) => {
  const dataWithLast = data.map((point, i) => ({
    ...point,
    isLast: i === data.length - 1,
  }));
  const yAxisDomain = getSourceUsageYAxisDomain(data, series);

  return (
    <div
      className={classNames(styles["overview_source_usage_chart"], className)}
    >
      {!hideLegend && (
        <div className={styles["overview_source_usage_chart__legend"]} role="list" aria-label="Chart series">
          {series.map((item) => (
            <div
              key={item.dataKey}
              className={styles["overview_source_usage_chart__legend_item"]}
              role="listitem"
            >
              <span
                className={styles["overview_source_usage_chart__legend_swatch"]}
                style={{ backgroundColor: item.color }}
              />
              <span
                className={styles["overview_source_usage_chart__legend_label"]}
              >
                {item.name}
              </span>
            </div>
          ))}
          {!hideMoreSourcesLegend && (
            <div
              className={classNames(
                styles["overview_source_usage_chart__legend_item"],
                styles["overview_source_usage_chart__legend_item_more"]
              )}
              role="listitem"
            >
              <span className={styles["overview_source_usage_chart__legend_label"]}>
                + 24 more sources
              </span>
            </div>
          )}
        </div>
      )}

      <div className={styles["overview_source_usage_chart__body"]}>
        <div className={styles["overview_source_usage_chart__axis_label"]}>
          Usage
        </div>

        <div className={styles["overview_source_usage_chart__chart"]}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={dataWithLast}
              margin={{ left: 14, top: 18 }}
            >
              <Tooltip content={<OverviewSourceUsageTooltip />} />
              <CartesianGrid strokeDasharray="2 2" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={{ stroke: "#BEC8CF" }}
                tick={{ fontSize: 16, fill: "#7F94A1" }}
              />
              <YAxis
                type="number"
                domain={yAxisDomain}
                tickCount={6}
                allowDataOverflow
                tickLine={false}
                axisLine={{ stroke: "#BEC8CF" }}
                tick={{ fontSize: 16, fill: "#7F94A1" }}
              />
              {series.map((item) => (
                <Line
                  key={item.dataKey}
                  type="monotone"
                  dataKey={item.dataKey}
                  name={item.name}
                  stroke={item.color}
                  strokeWidth={9}
                  dot={<OverviewSourceUsageDot fillColor={item.color} />}
                  activeDot={<OverviewSourceUsageDot fillColor={item.color} active />}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default OverviewSourceUsageChart;
